// @flow
import * as React from 'react'
import cn from 'classnames'
import Color from 'color'
import { useBlock } from '@shift-marketing/shift-one-website-editor'

import styles from './styles.module.scss'
import Button from '../Button'

import withTheme from 'hoc/withTheme'
import supportedThemes, { Theme } from './themes/__supportedThemes'
import propertiesSchema from './properties.schema.json'

type FormProps = {
    onClick?: (e: SyntheticEvent) => void,
    onSubmit?: (e: SyntheticEvent) => void,
    children?: ?React.Node,
    theme: Theme
};

export default withTheme(supportedThemes)(function Form (props: FormProps) {
    const { properties } = useBlock('form', propertiesSchema)

    return (
        <form
            onClick={props.onClick}
            onSubmit={props.onSubmit}
            className={styles.form}
            style={{
                '--primary-color': props.theme.primaryColor,
                '--color-selected': properties.activeTextColor,
                '--title-color': properties.titleColor,
                '--tab-color': properties.tabColor,
                '--description-color': properties.descriptionColor,
                '--field-background-color': properties.fieldColor,
                '--field-text-color': properties.textColor,
                '--focused-field-text-color': properties.activeTextColor,
                '--focused-field-background-color': props.theme.focusedFieldBgColor,
                '--field-hover-background-color': Color(properties.fieldColor).alpha(.25).rgb().string(),
                '--placeholder-text-color': properties.textColor,
                '--checkbox-background-color': properties.fieldColor,
                '--dropdown-indicator': properties.fieldColor,
            }}
        >
            {props.children}
        </form>
    )
})

export function Row ({ children }: { children: React.Children }) {
    return <div className={styles.row}>{children}</div>
}

export function RowColumns ({ children, className, columnsTablet, columnsMobile }: { children: React.Children }) {
    return <div className={cn(styles.rowColumns, className, {
        [styles.__columns_tablet_3]: columnsTablet === 3,
        [styles.__columns_tablet_2]: columnsTablet === 2,
        [styles.__columns_tablet_1]: columnsTablet === 1,
        [styles.__columns_mobile_3]: columnsMobile === 3,
        [styles.__columns_mobile_2]: columnsMobile === 2,
        [styles.__columns_mobile_1]: columnsMobile === 1
    })}>{children}</div>
}

export const RowColumn = React.forwardRef((props: { children: React.Children, size?: number, padding?: number, className?: string, }, ref) => (
    <div
        className={cn(styles.rowColumn, props.className)}
        style={{ flexBasis: props.size * 100 + '%', padding: props.padding }}
        ref={ref}
    >
        {props.children}
    </div>
))

type SubmitProps = {
    id?: string,
    isLoading?: boolean,
    label?: string,
    color?: string,
    size?: string
};

export function Divider () {
    return <div className={styles.divider}/>
}

export const Submit = withTheme(supportedThemes)(function (props: SubmitProps) {
    return (
        <Button id={props.id || null} isSubmit disabled={props.isLoading} loading={props.isLoading} color='primary5' onClick={() => {
        }} style={props.style || { backgroundColor: props.theme.buttonColor, color: props.theme.buttonTextColor }}>{props.label}</Button>
    )
})

Submit.defaultProps = {
    isLoading: false,
    color: 'primary',
    size: 'medium',
    cancel: 'Cancel',
    submit: 'Submit'
}

export { default as Field, CurrencyField } from './Fields/Field'
export { default as Label } from './Label'
export { default as Panel } from './Panel'
export { default as Tabs } from './Tabs'
export { default as RadioButtons } from './RadioButtons'
export { default as Choices } from './Fields/Choices'
export { default as Dropdown } from './Fields/Dropdown'
export { default as ModelPickerDropdown } from './Fields/ModelPickerDropdown'
export { default as Checkbox } from './Fields/Checkbox'
export { default as Textarea } from './Fields/Textarea'
export { default as SubmitBlock } from './SubmitBlock'
